import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ConfigurationReportTab from './configurationTab';
import Sorting from '../../components/company_panel/common/sorting';
import {
    capitalizeAllFirstLetters,
    capitalizeFirstLetter,
    checkPermission,
    erpErrorText,
    formateDate,
    formateDateWithTime,
    formatToTwoDecimals,
    getBrandId,
    getCompany,
    getOutletId
} from '../../helpers/commonFunctions';
import CustomPagination from '../../components/company_panel/common/custompagination';
import Loader from '../../components/company_panel/common/loader';
import notFound from '../../../src/assets/company/img/no-record-found.png'
import CustomRangepicker from '../../components/company_panel/common/rangepicker';
import { erpChangeStatus, erpList, erpPaymentList, InventoryEnd, ListInventoryReport, StockMovementReport, erpCommissionsList, RegenerateAll } from '../../services/company_panel/report.services';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { LOADER_TIME, SOCKET_EVENTS } from '../../utils/Constants';
import { SOCKET_CLIENT } from '../../utils/socket';
import $ from "jquery";
import { GetParentCategories } from '../../services/company_panel/categoryService';
import { GetAllSuppliers } from '../../services/company_panel/supplierService';
import Swal from 'sweetalert2';
import { Tooltip, Whisper } from "rsuite";
import { GetCompanyProfileDetail } from '../../services/company_panel/companyService';
import { lastBusinessDate } from '../../services/company_panel/outletService';

const ERPConfigurationList = () => {

    const breadcrumbs = [{ title: "Dashboard", url: "/company/dashboard" }, { title: "Pages", url: "" }];
    const [dataLength, setDataLength] = useState();
    const [itemPerPage, setItemPerPage] = useState(10);
    const [page, setPage] = useState(1);
    const [businessDate, setBusinessDate] = useState({});
    const [invoiceLineData, setInvoiceLineData] = useState([]);
    const [statsUpdate, setStatusUpdate] = useState(false);
    const [loader, setLoader] = useState(true);
    const [isOutletChanged, setIsOutletChanged] = useState(false);
    const [outletName, setOutletName] = useState("");
    const [selectedRecords, setSelectedRecords] = useState([]);
    const params = useParams();
    const [reportType, setReportType] = useState('sales');
    const [globalSearch, setGlobalSearch] = useState("");
    const [search, setSearch] = useState({});
    const [paymentLineData, setPaymentLineData] = useState([]);
    const [commissionsLineData, setCommissionsLineData] = useState([]);
    const [outletData, setOutletData] = useState("");
    const [businessDateFilterValue, setBusinessDateFilterValue] = useState([]);
    const [resetdate, setResetDate] = useState(false);
    const [selectedPaymentRecords, setSelectedPaymentRecords] = useState([]);
    const [selectedCommissionsRecords, setSelectedCommissionsRecord] = useState([]);
    const [companyData, setCompanyData] = useState({});
    const [selectAll, setSelectAll] = useState(false);
    const [lineData, setLineData] = useState(null);

    // Live "Sync to ERP" progress. The sync API returns immediately (just a jobId);
    // the real result arrives over the GET-DATA socket channel. syncProgress drives
    // the progress card; syncing disables the sync/upload/reverse buttons meanwhile.
    const [syncProgress, setSyncProgress] = useState(null);
    const [syncing, setSyncing] = useState(false);
    const syncJobIdRef = useRef(null);

    // reverse lookup: socket event name -> { erp_type, kind } for every ERP sync event
    const erpEventMap = useMemo(() => {
        const map = {};
        Object.entries(SOCKET_EVENTS.ERP).forEach(([reportType, events]) => {
            const erpType = reportType.toLowerCase();
            map[events.SYNC_PROGRESS] = { erp_type: erpType, kind: 'progress' };
            map[events.SYNC_COMPLETED] = { erp_type: erpType, kind: 'completed' };
            map[events.SYNC_ERROR] = { erp_type: erpType, kind: 'error' };
        });
        return map;
    }, []);

    // Listen for ERP sync progress / completion / error on the company register room
    // (joined globally in companyLayout). We only react to the job this page started.
    useEffect(() => {
        const handleErpSync = (response) => {
            console.log(response);
            const matched = erpEventMap[response?.event];
            const data = response?.data;
            if (!matched || !data) return;
            // only react to the sync this page triggered
            if (syncJobIdRef.current && data.jobId && data.jobId !== syncJobIdRef.current) return;

            if (matched.kind === 'progress') {
                setSyncProgress({
                    erp_type: matched.erp_type,
                    status: 'progress',
                    message: response?.message,
                    percent: Number(data.percent) || 0,
                    total: Number(data.total) || 0,
                    processed: Number(data.processed) || 0,
                    synced: Number(data.synced) || 0,
                    failed: Number(data.failed) || 0,
                    failed_records_messages: [],
                });
            } else if (matched.kind === 'completed') {
                setSyncProgress({
                    erp_type: matched.erp_type,
                    status: 'completed',
                    message: response?.message,
                    percent: 100,
                    total: Number(data.total) || 0,
                    processed: Number(data.processed) || 0,
                    synced: Number(data.synced) || 0,
                    failed: Number(data.failed) || 0,
                    // sales & commissions carry per-record failures; payments never does
                    failed_records_messages: Array.isArray(data.failed_records_messages) ? data.failed_records_messages : [],
                });
                setSyncing(false);
                syncJobIdRef.current = null;
                setStatusUpdate(prev => !prev);
            } else if (matched.kind === 'error') {
                // full-sync failure — the reason is top-level, not inside data
                setSyncProgress({
                    erp_type: matched.erp_type,
                    status: 'error',
                    message: response?.message || 'ERP sync failed',
                });
                setSyncing(false);
                syncJobIdRef.current = null;
                setStatusUpdate(prev => !prev);
            }
        };

        SOCKET_CLIENT.on('GET-DATA', handleErpSync);
        return () => {
            SOCKET_CLIENT.off('GET-DATA', handleErpSync);
        };
    }, [erpEventMap]);

    // Kick off an ERP sync: the HTTP call only means "job started". Validation errors
    // still come back synchronously (success: 0); everything else is driven by sockets.
    const beginErpSync = async (formData, erpType) => {
        // one sync at a time — block a second job while one is still running
        if (syncing) {
            Swal.fire({
                customClass: 'swal-wide',
                icon: 'warning',
                title: "A sync is already in progress",
                showConfirmButton: false,
                timer: 2000,
                toast: true,
                position: 'top-right',
            });
            return;
        }
        setSelectAll(false);
        setSyncing(true);
        syncJobIdRef.current = null;
        // don't render the progress card yet — it appears only when the first real
        // progress event arrives over the socket. The button shows "Syncing…" meanwhile.
        setSyncProgress(null);
        try {
            const data = await InventoryEnd(formData);
            const body = data?.data || {};
            // synchronous validation failure (missing ids, invalid erp_type, no business date, ...)
            if (body?.success === 0 || body?.success === '0' || body?.statuscode === 400) {
                Swal.fire({
                    customClass: 'swal-wide',
                    icon: 'error',
                    title: erpErrorText(body?.error) || body?.message || 'Sync failed',
                    showConfirmButton: false,
                    timer: 2000,
                    toast: true,
                    position: 'top-right',
                });
                setSyncing(false);
                setSyncProgress(null);
                return;
            }
            const jobId = body?.data?.jobId || body?.jobId;
            if (jobId) {
                syncJobIdRef.current = jobId;
            }
            Swal.fire({
                customClass: 'swal-wide',
                icon: 'success',
                title: body?.message || 'Syncing Started',
                showConfirmButton: false,
                timer: 2000,
                toast: true,
                position: 'top-right',
            });
        } catch (error) {
            console.log("error ====> ", error);
            Swal.fire({
                customClass: 'swal-wide',
                icon: 'error',
                title: erpErrorText(error?.response?.data?.errors) || erpErrorText(error) || 'Sync failed',
                showConfirmButton: false,
                timer: 2000,
                toast: true,
                position: 'top-right',
            });
            setSyncing(false);
            setSyncProgress(null);
        }
    };

    const handleReportChange = (type) => {
        setReportType(type);
        setItemPerPage(10);
    };

    useMemo(() => {
        document.addEventListener('outlet-changed', function () {
            setIsOutletChanged(prev => !prev);
        })
    }, []);

    const resetFilter = (e) => {
        e.preventDefault()
        setGlobalSearch("")
        prepareSearch()
        setSearch({})
        setResetDate(!resetdate)
        if (reportType == 'sales') {
            $("#resetFilter")[0].selectedIndex = 0
        }
    }

    const prepareSearch = (key, value) => {
        let sr = { ...search };
        if (String(value).length > 0) {
            sr[key] = value;
        } else {
            delete sr[key];
        }
        setSearch(sr);
    }

  // Sales Data Get API
    useEffect(() => {
        if(reportType === 'sales'){
            setSelectAll(false);
            const getData = setTimeout(() => {
                setLoader(true);
                const formData = new FormData();
                formData.append("page", page)
                formData.append("per_page", itemPerPage)
                formData.append('outlet_id', getOutletId());
                formData.append('report_type', reportType);
                formData.append("global_search", globalSearch)
                formData.append("search", JSON.stringify(search))
                erpList(formData).then(data => {
                    const response = data && data.data && data.data.response;
                    setOutletName(response ? response.outlet_name : "");
                    setInvoiceLineData(response ? response.invoice_lines_list : []);
                    setDataLength(response ? response.count : 0);
                    setSelectedRecords([]);
                    setTimeout(() => {
                        setLoader(false)
                    }, LOADER_TIME);
                }).catch(error => {
                    console.log("error ====> ", error);
                    setTimeout(() => {
                        setLoader(false)
                    }, LOADER_TIME);

                    setOutletName("");
                    setInvoiceLineData([]);
                    setDataLength(0);
                    setSelectedRecords([]);

                    Swal.fire({
                        customClass: 'swal-wide',
                        position: 'absolute',
                        icon: 'error',
                        title: erpErrorText(error?.response?.data?.errors) || erpErrorText(error),
                        showConfirmButton: false,
                        timer: 1000,
                        toast: true,
                        position: 'top-right',
                    })
                })
            }, 300);
            return () => clearTimeout(getData);
        }
    }, [isOutletChanged, page, itemPerPage, statsUpdate, globalSearch, params.id, search,reportType])


 // Payments Data Get API
    useEffect(() => {
        if(reportType === 'payments'){
            setSelectAll(false);
            const getData = setTimeout(() => {
                setLoader(true);
                const formData = new FormData();
                formData.append("page", page)
                formData.append("per_page", itemPerPage);
                formData.append('outlet_id', getOutletId());
                formData.append("global_search", globalSearch)
                formData.append("search", JSON.stringify(search))
                erpPaymentList(formData).then(data => {
                    const response = data && data.data && data.data.response;
                    setOutletData(response ? response.outlet_name : "");
                    setPaymentLineData(response ? response.payments_line_list : []);
                    setDataLength(response ? response.count : 0);
                    setSelectedPaymentRecords([]);
                    // setERPEnabled(response.erp_enabled);
                    setTimeout(() => {
                        setLoader(false)
                    }, LOADER_TIME);
                }).catch(error => {
                    console.log("error ====> ", error);

                    setOutletData("");
                    setPaymentLineData([]);
                    setDataLength(0);
                    setSelectedPaymentRecords([]);

                    setTimeout(() => {
                        setLoader(false)
                    }, LOADER_TIME);
                })
            }, 300);
            return () => clearTimeout(getData);
        }
    }, [isOutletChanged, page, itemPerPage, statsUpdate, globalSearch, params.id, search,reportType])


 // Commissions Data Get API
    useEffect(() => {
        if(reportType === 'commissions'){
            setSelectAll(false);
            const getData = setTimeout(() => {
                setLoader(true);
                const formData = new FormData();
                formData.append("page", page)
                formData.append("per_page", itemPerPage)
                formData.append('outlet_id', getOutletId());
                formData.append("global_search", globalSearch)
                formData.append("search", JSON.stringify(search))
                erpCommissionsList(formData).then(data => {
                    const response = data && data.data && data.data.response;
                    setOutletData(response ? response.outlet_name : "");
                    setCommissionsLineData(response ? response.commissions_line_list : []);
                    setDataLength(response ? response.count : 0);
                    setSelectedCommissionsRecord([]);
                    // setERPEnabled(response.erp_enabled);
                    setTimeout(() => {
                        setLoader(false)
                    }, LOADER_TIME);
                }).catch(error => {
                    console.log("error ====> ", error);

                    setOutletData("");
                    setCommissionsLineData([]);
                    setDataLength(0);
                    setSelectedCommissionsRecord([]);

                    setTimeout(() => {
                        setLoader(false)
                    }, LOADER_TIME);
                })
            }, 300);
            return () => clearTimeout(getData);
        }
    }, [isOutletChanged, page, itemPerPage, statsUpdate, globalSearch, params.id, search,reportType])

// Toggle individual checkbox selection using array index as identifier for sales & payments & commissions
    const handleCheckboxChange = (index) => {
        if (reportType === 'sales') {
            setSelectedRecords(prev => {
                return prev.includes(index)
                    ? prev.filter(id => id !== index)
                    : [...prev, index];
            });
        } else if (reportType === 'payments') {
            setSelectedPaymentRecords(prev => {
                return prev.includes(index)
                    ? prev.filter(id => id !== index)
                    : [...prev, index];
            });
        } else if (reportType === 'commissions') {
            setSelectedCommissionsRecord(prev => {
                return prev.includes(index)
                    ? prev.filter(id => id !== index)
                    : [...prev, index];
            });
        }
    };

    const selectAllRecordsToSync = (checked) => {
        setSelectAll(checked);
        let records = [];
        if (reportType === 'sales') {
            if(checked){
                records = Object.keys(invoiceLineData).map(i => +i);
                setSelectedRecords(records);
            }else{
                setSelectedRecords([])
            }
        } else if (reportType === 'payments') {
            if(checked){
                records = Object.keys(paymentLineData).map(i => +i);
                setSelectedPaymentRecords(records);
            }else{
                setSelectedPaymentRecords([])
            }
        } else if (reportType === 'commissions') {
            if(checked){
                records = Object.keys(commissionsLineData).map(i => +i);
                setSelectedCommissionsRecord(records);
            }else{
                setSelectedCommissionsRecord([])
            }
        }
    };

    // Sync To ERP For Sales Data
    const handleSyncToERP = async (isReverseSync, singleRecord = null) => {
        if (!(isReverseSync === true || isReverseSync === "true") && selectedRecords.length === 0) {
            Swal.fire({
                customClass: 'swal-wide',
                icon: 'warning',
                title: "Please select at least one record to sync",
                showConfirmButton: false,
                timer: 2000,
                toast: true,
                position: 'top-right',
            });
            return;
        }
        const formData = new FormData();
        formData.append('outlet_id', getOutletId());

        const flatData = invoiceLineData.flat();
        // let selectedOrderModeIds = [];


        // if (isReverseSync === true || isReverseSync === "true") {
        //     selectedOrderModeIds = [{
        //         order_mode_id: singleRecord.orderModeID,
        //         business_date: singleRecord.business_date
        //     }];
        // } else {
        //     selectedOrderModeIds = selectedRecords.map(index => ({
        //         order_mode_id: flatData[index].orderModeID,
        //         business_date: flatData[index].business_date
        //     }));
        // }
        // formData.append('order_mode_ids', JSON.stringify(selectedOrderModeIds));

        let invoice_line_ids = [];
        if (isReverseSync === true || isReverseSync === "true") {
            invoice_line_ids.push(singleRecord.id);
        } else {
            invoice_line_ids = selectedRecords.map(index => flatData[index].id);
        }
        formData.append('invoice_line_ids', JSON.stringify(invoice_line_ids));

        if (isReverseSync === true || isReverseSync === "true") {
            formData.append('is_reverse_sync', isReverseSync);
        }
        formData.append('erp_type', reportType);
        setSelectedRecords([]);
        await beginErpSync(formData, reportType);
    };
    // Sync To ERP Uploaded Again For Sales Data & Payments Data & Commissions Data
    const handleERPUploadAgain = async (singleRecord = null) => {
        const formData = new FormData();
        // let selectedOrderModeIds = [];
        let selectedTenderIds = [];
        if (reportType === 'sales') {
            // selectedOrderModeIds = [{
            //     order_mode_id: singleRecord.orderModeID,
            //     business_date: singleRecord.business_date
            // }];
            // formData.append('order_mode_ids', JSON.stringify(selectedOrderModeIds));
            formData.append('invoice_line_ids', JSON.stringify([singleRecord.id]));
            formData.append('outlet_id', getOutletId());
            formData.append('erp_type', 'sales');
        } else if (reportType === 'payments') {
            // selectedTenderIds = [{
            //     tender_id: singleRecord.tender_id,
            //     business_date: singleRecord.business_date,
            //     total_amount : singleRecord.total_amount,
            //     payment_method : singleRecord.payment_method,
            //     tender_sku : singleRecord.tender_sku,
            // }];
            formData.append('outlet_id', getOutletId());
            // formData.append('payments', JSON.stringify(selectedTenderIds));
            formData.append('payment_line_ids', JSON.stringify([singleRecord.id]));
            formData.append('erp_type', 'payments');
        }
        else if (reportType === 'commissions') {
            // selectedTenderIds = [{
            //     tender_id: singleRecord.tender_id,
            //     business_date: singleRecord.business_date,
            //     total_amount : singleRecord.total_amount,
            //     payment_method : singleRecord.payment_method,
            // }];
            formData.append('outlet_id', getOutletId());
            // formData.append('commissions', JSON.stringify(selectedTenderIds));
            formData.append('commission_line_ids', JSON.stringify([singleRecord.id]));
            formData.append('erp_type', 'commissions');
        }
        await beginErpSync(formData, reportType);
    };

    const ChangeStatus = () => {
        Swal.fire({
            customClass: 'swal-wide',
            title: 'Are you sure?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#2f9bff',
            cancelButtonColor: '#dc3545',
            confirmButtonText: 'Yes'
        }).then((result) => {
            if (result.isConfirmed) {
                erpChangeStatus().then(data => {
                    if (data.status === 200) {
                        Swal.fire({
                            customClass: 'swal-wide',
                            position: 'absolute',
                            icon: 'success',
                            title: data.data.message,
                            showConfirmButton: false,
                            timer: 2000,
                            toast: true,
                            position: 'top-right',
                        })

                        setStatusUpdate(!statsUpdate)
                    }
                }).catch(error => {
                    console.log("error===>", error);
                })
            }
        })
    }

    const handleFilterChange = (e) => {
        if (e?.target.value) {
            prepareSearch("erp_filter", e.target.value);
        } else {
            prepareSearch("erp_filter", "");
        }
    };
   

    // Sync To ERP For Payments Data
    const handleSyncToERPPaymentData = async () => {
        if (selectedPaymentRecords.length === 0) {
            Swal.fire({
                customClass: 'swal-wide',
                icon: 'warning',
                title: "Please select at least one record to sync",
                showConfirmButton: false,
                timer: 2000,
                toast: true,
                position: 'top-right',
            });
            return;
        }
        const formData = new FormData();
        formData.append('outlet_id', getOutletId());
        const flatPaymentData = paymentLineData.flat();
        const paymentLineIds = selectedPaymentRecords.map(index => flatPaymentData[index].id);
        formData.append('payment_line_ids', JSON.stringify(paymentLineIds));
        formData.append('erp_type', reportType);
        setSelectedPaymentRecords([]);
        await beginErpSync(formData, reportType);
    };

    useEffect(() => {
        const id = JSON.parse(localStorage.getItem("company_detail"))

        GetCompanyProfileDetail(id.id).then(data => {
            setCompanyData(data && data.data && data.data.response ? data.data.response : [])
        }).catch(error => {
            console.log("error ====> ", error);
        })

    }, [statsUpdate])

    useEffect(() => {
        const formData = new FormData();
        formData.append('outlet_id', getOutletId());
        lastBusinessDate(formData).then(data => {
            const fetchedBusinessDates = data?.data?.response || [];
            // const formattedBusinessDate = fetchedBusinessDates.business_date ? new Date(fetchedBusinessDates.business_date).toISOString().split('T')[0] : '';
            const businessDate = new Date(fetchedBusinessDates.business_date);
            const year = businessDate.getFullYear();
            const month = String(businessDate.getMonth() + 1).padStart(2, '0');
            const day = String(businessDate.getDate()).padStart(2, '0');
            const formattedBusinessDate = `${year}-${month}-${day}`;
            
            if (formattedBusinessDate) {
                setBusinessDate(formattedBusinessDate);
            } else {
                setBusinessDate('');
            }
        }).catch(error => {
            console.log("Error fetching business dates:", error);
        });
    }, [isOutletChanged]);

    // Sync To ERP For Commissions Data
    const handleSyncToERPCommissionsData = async () => {
        if (selectedCommissionsRecords.length === 0) {
            Swal.fire({
                customClass: 'swal-wide',
                icon: 'warning',
                title: "Please select at least one record to sync",
                showConfirmButton: false,
                timer: 2000,
                toast: true,
                position: 'top-right',
            });
            return;
        }
        const formData = new FormData();
        formData.append('outlet_id', getOutletId());
        formData.append('erp_type', reportType);

        const commission_line_ids = selectedCommissionsRecords.map(index => commissionsLineData[index].id);
        formData.append('commission_line_ids', JSON.stringify(commission_line_ids));
        setSelectedCommissionsRecord([]);
        await beginErpSync(formData, reportType);
    };

    // const viewLineData=(line, lineType)=>{
    //     setLineData(line);
    // }
     const handleRegenerateAll = async (reportType, singleRecord = null) => {
        setSelectAll(false);
        setLoader(true);
        const formData = new FormData();
        formData.append('erp_type', reportType);

        if(reportType == 'sales'){
            if(singleRecord){
                const item = singleRecord.aggregator_id
                    ? { aggregator_id: singleRecord.aggregator_id, id: singleRecord.id }
                    : { order_mode_id: singleRecord.order_mode_id, id: singleRecord.id };
                formData.append('invoice_line_ids', JSON.stringify([item]));
            } else {
                if(!selectedRecords?.length)return;
                const flatData = invoiceLineData.flat();
                const invoice_line_ids = selectedRecords.map(index => {
                    if(flatData[index].aggregator_id){
                        return {
                            aggregator_id: flatData[index].aggregator_id,
                            id: flatData[index].id
                        }
                    }else{
                        return {
                            order_mode_id: flatData[index].order_mode_id,
                            id: flatData[index].id
                        }
                    }
                });
                formData.append('invoice_line_ids', JSON.stringify(invoice_line_ids));
            }
        }else if(reportType == 'payments'){
            if(singleRecord){
                formData.append('payment_line_ids', JSON.stringify([singleRecord.id]));
            } else {
                if(!selectedPaymentRecords?.length)return;
                const flatPaymentData = paymentLineData.flat();
                const paymentLineIds = selectedPaymentRecords.map(index => flatPaymentData[index].id);
                formData.append('payment_line_ids', JSON.stringify(paymentLineIds));
            }
        }else if(reportType == 'commissions'){
            if(singleRecord){
                formData.append('commission_line_ids', JSON.stringify([singleRecord.id]));
            } else {
                if(!selectedCommissionsRecords?.length)return;
                const commission_line_ids = selectedCommissionsRecords.map(index => commissionsLineData[index].id);
                formData.append('commission_line_ids', JSON.stringify(commission_line_ids));
            }
        }

        try {
            const data = await RegenerateAll(formData);
            console.log(data);
            if (data?.status === 200) {
                Swal.fire({
                    customClass: 'swal-wide',
                    icon: data.data.statuscode === 400 ? 'error' : 'success',
                    title: erpErrorText(data?.data?.error) || data?.data?.message,
                    showConfirmButton: false,
                    timer: 2000,
                    toast: true,
                    position: 'top-right',
                });

                // if (data.data.statuscode !== 400) {
                //     setIsReversed(true);
                // }

                // setStatus(true);
                setSelectedCommissionsRecord([]);
                setStatusUpdate(!statsUpdate);
            }
            setLoader(false);
        } catch (error) {
            console.log("error ====> ", error);
            setLoader(false);
            // setStatus(false);
        }
    };


    const closeModel = (modalId) => {
        $(modalId).hide();
        $('.modal-backdrop').remove();
        // setShowDefault({});
        // setStatusUpdate(!statsUpdate)
    }

    const openModel = (modalId, lineData) => {
        // setActionType(type);
        $(modalId).show().css('opacity', 1).addClass('show');
        $('body').append('<div className="modal-backdrop fade show"></div>')
        // setOrderMode([]);
        setLineData(lineData);
    }

    return (
        <>
            <div className="animation_fade">
                <div className="page-header d-flex align-items-center">
                    <div className="page-header-left"> 

                        <h4>Report</h4>
                    </div>
                </div>
                <div className="card custom-card overflow-hidden">
                    <div className="gap-20 border-bottom">
                        <div className="col-12">
                            <div className="d-flex justify-content-between align-items-center">
                                <ConfigurationReportTab tab="erp" />
                            </div>
                        </div>
                    </div>
                    
                    <div className="gap-20 d-flex justify-content-between">
                        <div className='page-header d-flex align-items-center mb-0'>
                            <div className='d-flex gap-3 align-items-center'>
                                <div className="form-group mb-0 rangepicker_container">
                                    <input type="search" className="form-control" value={globalSearch} placeholder="Search" onChange={(e) => { setGlobalSearch(e.target.value) }} />
                                </div>
                            </div>
                            {reportType == 'sales' ?
                                <div className="ms-2">
                                    <div className="form-group mb-0 filter_icon_container filter_design">
                                        <select
                                            className="form-control cp"
                                            onChange={handleFilterChange}
                                            id="resetFilter"
                                            style={{ width: "180px" }}
                                        >
                                            <option value="">Status Filter</option>
                                            <option value="uploaded">Uploaded</option>
                                            <option value="not_uploaded">Not Uploaded</option>
                                        </select>
                                    </div>
                                </div>
                                : null}
                            <div className="date-input ms-3">
                                <CustomRangepicker business_date={true} value={businessDateFilterValue} GetDateRange={(e) => { prepareSearch("business_date", e) }}
                                    resetdate={resetdate} maxDate={businessDate} />
                            </div>
                            <button type="reset" value='Reset' onClick={resetFilter} className="btn btn-primary mr-2 ms-5">Reset Filter</button>
                        </div>

                        {checkPermission('store_erp', 'edit') && (
                            <div className="form-check form-switch d-flex justify-content-start">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="status-change"
                                    name="status"
                                    checked={companyData?.erp_enabled ? "checked" : ""}
                                    onClick={() => { ChangeStatus() }}
                                />
                                <label
                                    className="form-check-label"
                                    for="status-change"
                                >{companyData?.erp_enabled ? 'Disable' : 'Enable'} Sales Auto Upload</label>
                            </div>
                        )}
                    </div>
                    <div className='gap-20 ps-0 pb-0 pe-0'>
                        <ul className='d-flex setting submenu-list border-bottom mb-0 p-0'>
                            <li className={reportType === 'sales' ? 'active' : ''} onClick={() => handleReportChange('sales')}>Sales</li>
                            <li className={reportType === 'payments' ? 'active' : ''} onClick={() => handleReportChange('payments')}>Payments</li>
                            <li className={reportType === 'commissions' ? 'active' : ''} onClick={() => handleReportChange('commissions')}>Commissions</li>
                        </ul>
                    </div>

                   {checkPermission('store_erp', 'edit') && (
                   <div className="sync-container my-3 ms-2 d-flex justify-content-end align-items-center me-2 gap-2">
                        <button
                            className="btn btn-primary"
                            type="button"
                            onClick={() => handleRegenerateAll(reportType)}
                        >
                            Regenerate
                        </button>

                        <button
                            className="btn btn-primary"
                            type="button"
                            disabled={syncing}
                            onClick={
                            reportType === "sales"
                                ? handleSyncToERP
                                : reportType === "payments"
                                ? handleSyncToERPPaymentData
                                : reportType === "commissions"
                                ? handleSyncToERPCommissionsData
                                : null
                            }
                        >
                            {syncing ? 'Syncing…' : 'Sync To ERP'}
                        </button>
                    </div>
                   )}

                   {/* Live ERP sync progress / result — driven by GET-DATA socket events */}
                   {syncProgress && (() => {
                        const status = syncProgress.status;
                        const isDone = status === 'completed';
                        const isError = status === 'error';
                        const percent = isError ? 100 : (syncProgress.percent || 0);
                        const accent = isError ? '#ef4444' : isDone ? '#22c55e' : '#2f9bff';
                        const erpLabel = capitalizeFirstLetter(syncProgress.erp_type || reportType);
                        const failedRecords = syncProgress.failed_records_messages || [];
                        return (
                            <div className="mx-2 mb-3" style={{
                                background: '#fff', border: '1px solid #e7ecf3', borderLeft: `4px solid ${accent}`,
                                borderRadius: '12px', padding: '14px 18px',
                                boxShadow: '0 2px 10px rgba(16, 30, 54, 0.06)'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                    <div style={{
                                        width: '40px', height: '40px', flexShrink: 0, borderRadius: '50%',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        background: isError ? 'rgba(239,68,68,0.12)' : isDone ? 'rgba(34,197,94,0.12)' : 'rgba(47,155,255,0.12)', color: accent
                                    }}>
                                        {isError ? (
                                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
                                        ) : isDone ? (
                                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
                                        ) : (
                                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" style={{ animation: 'spin 0.9s linear infinite' }}><path d="M21 12a9 9 0 1 1-6.219-8.56" /></svg>
                                        )}
                                    </div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                                            <span style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                                                {/* ERP type label */}
                                                <span style={{
                                                    flexShrink: 0, fontWeight: 700, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.4px',
                                                    padding: '3px 10px', borderRadius: '999px', color: accent,
                                                    background: isError ? 'rgba(239,68,68,0.12)' : isDone ? 'rgba(34,197,94,0.12)' : 'rgba(47,155,255,0.12)'
                                                }}>{erpLabel}</span>
                                                {/* backend message, shown separately */}
                                                <span style={{ fontWeight: 600, color: '#1f2d3d', fontSize: '14px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {syncProgress.message
                                                        || (isError ? 'ERP sync failed' : isDone ? 'ERP sync completed' : 'Syncing to ERP')}
                                                </span>
                                            </span>
                                            <span style={{ display: 'flex', alignItems: 'center', gap: '10px', whiteSpace: 'nowrap' }}>
                                                {!isError && <span style={{ fontWeight: 700, color: accent, fontSize: '14px' }}>{percent}%</span>}
                                                {(isDone || isError) && (
                                                    <button type="button" className="btn-close" style={{ fontSize: '10px' }} aria-label="Close" onClick={() => setSyncProgress(null)}></button>
                                                )}
                                            </span>
                                        </div>
                                        {!isError && syncProgress.total ? (
                                            <div style={{ fontWeight: 400, color: '#8a97a8', fontSize: '13px', marginBottom: '8px' }}>
                                                {(syncProgress.processed || 0).toLocaleString()} / {Number(syncProgress.total).toLocaleString()} records
                                                {' · '}<span style={{ color: '#22c55e' }}>{(syncProgress.synced || 0).toLocaleString()} synced</span>
                                                {' · '}<span style={{ color: '#ef4444' }}>{(syncProgress.failed || 0).toLocaleString()} failed</span>
                                            </div>
                                        ) : null}
                                        {isError ? (
                                            <div style={{ fontSize: '13px', color: '#b91c1c' }}>{syncProgress.message}</div>
                                        ) : (
                                            <div style={{ height: '8px', background: '#eef2f7', borderRadius: '999px', overflow: 'hidden' }}>
                                                <div style={{
                                                    height: '100%', width: `${percent}%`, borderRadius: '999px',
                                                    background: isDone ? accent : 'linear-gradient(90deg, #2f9bff, #6cb8ff)',
                                                    transition: 'width 0.4s ease'
                                                }} />
                                            </div>
                                        )}
                                    </div>
                                </div>
                                {/* per-record failures (sales & commissions only) */}
                                {isDone && failedRecords.length > 0 && (
                                    <div style={{ marginTop: '12px', borderTop: '1px solid #f0f0f0', paddingTop: '10px' }}>
                                        <div style={{ fontWeight: 600, color: '#b91c1c', fontSize: '13px', marginBottom: '6px' }}>
                                            Failed records ({failedRecords.length})
                                        </div>
                                        <div style={{ maxHeight: '160px', overflowY: 'auto' }}>
                                            {failedRecords.map((rec, idx) => (
                                                <div key={idx} style={{ fontSize: '12px', color: '#6b7280', padding: '3px 0' }}>
                                                    <span style={{ fontWeight: 600, color: '#374151' }}>#{rec.id}</span> — {rec.error}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                            </div>
                        );
                   })()}


                    {reportType === 'sales' ? (
                        <div className="tabler-wrap">
                            <div className="table-responsive">
                                <table className="table table-custom">
                                    <thead>
                                        <tr>
                                            <th width="10%">
                                                <div className='d-flex gap-2'>
                                                        <input
                                                            type="checkbox"
                                                            id="selectAllForSales" 
                                                            name='selectAllForSales'
                                                            checked={selectAll}
                                                            onChange={(event) => selectAllRecordsToSync(event.target.checked)}
                                                            // disabled={line?.erp_details?.erp_synced || line?.erp_details?.erp_reversed}
                                                        />
                                                        <label htmlFor="selectAllForSales">Select All</label>
                                                </div>
                                            </th>
                                            <th width="15%">Business Date</th>
                                            <th width="15%">Outlet Name</th>
                                            <th width="15%">Main Account Display Value</th>
                                            <th width="15%">General Number</th>
                                            <th width="15%">Description</th>
                                            <th width="10%">Customer Account</th>
                                            <th width="15%">Unit Price</th>
                                            <th width="15%">Status</th>
                                            <th width="15%">Error</th>
                                            <th width="15%">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loader ? (
                                            <tr>
                                                <td colSpan={10}>
                                                    <Loader />
                                                </td>
                                            </tr>
                                        ) : (
                                            <>
                                                {invoiceLineData.flat().length > 0 ? (
                                                    invoiceLineData.flat().map((line, index) => (
                                                        <tr
                                                            key={index}
                                                            style={{
                                                                backgroundColor: line?.erp_details?.erp_reversed
                                                                    ? '#ADD8E6'
                                                                    : '',
                                                            }}
                                                        >
                                                            <td>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selectedRecords.includes(index)}
                                                                    onChange={() => handleCheckboxChange(index)}
                                                                    disabled={line?.erp_details?.erp_synced || line?.erp_details?.erp_reversed}
                                                                />
                                                            </td>
                                                            <td>{formateDate(line.business_date)}</td>
                                                            <td>{capitalizeFirstLetter(outletName)}</td>
                                                            <td>{line.main_account_display_value}</td>
                                                            <td>{line?.erp_details?.invoice_identifier || '-'}</td>
                                                            <td>{line.description}</td>
                                                            <td>{line.customer_account}</td>
                                                            <td>{Number(line.unit_price).toFixed(2) + ' SAR'}</td>
                                                            <td>
                                                                {!line?.erp_details || line?.erp_details?.error ? (
                                                                    <span className="text-danger">Not Uploaded</span>
                                                                ) : line?.erp_details?.erp_synced === true && !line?.erp_details?.erp_reversed ? (
                                                                    <span className="text-success">Uploaded</span>
                                                                ) : (
                                                                    <span className="text-warning">Reversed</span>
                                                                )}
                                                            </td>
                                                            {line?.erp_details?.error ? (
                                                                <td>
                                                                    <Whisper
                                                                        placement="top"
                                                                        controlId="control-id-hover"
                                                                        trigger="click"
                                                                        container={document.body}   // 👈 IMPORTANT
                                                                        flip={false}  
                                                                        preventOverflow
                                                                        speaker={
                                                                            <Tooltip style={{
                                                                                maxHeight: '200px',
                                                                                overflowY: 'auto',
                                                                                whiteSpace: 'normal',
                                                                                // maxWidth: '300px'
                                                                              }}>
                                                                                Unable to create free text invoice header. {erpErrorText(line?.erp_details?.error)}
                                                                            </Tooltip>
                                                                        }
                                                                    >
                                                                        <span className="field-more-info ms-1 cp">
                                                                            <i className="fas fa-exclamation-circle text-danger"></i>
                                                                        </span>
                                                                    </Whisper>
                                                                </td>
                                                            ) : (
                                                                <td>-</td>
                                                            )}
                                                            <td className="text-end">
                                                                <div className="dropdown d-inline-block">
                                                                    <button
                                                                        className="btn btn-icon btn-secondary"
                                                                        type="button"
                                                                        data-bs-toggle="dropdown"
                                                                        aria-expanded="false"
                                                                    >
                                                                        <i className="ti ti-dots-vertical"></i>
                                                                    </button>
                                                                    <ul className="dropdown-menu">
                                                                        <li>
                                                                            <div
                                                                                className="dropdown-item"
                                                                                onClick={() => openModel('#salesModal', line)}
                                                                            >
                                                                                <i className="ti ti-eye" aria-hidden="true"></i> View
                                                                            </div>
                                                                        </li>
                                                                        {/* <li>
                                                                            <div
                                                                                className="dropdown-item"
                                                                                data-bs-toggle="modal" data-bs-target="#salesModal"
                                                                                onClick={() => viewLineData(line)}
                                                                            >
                                                                                <i
                                                                                    className="ti ti-eye"
                                                                                    aria-hidden="true"
                                                                                ></i>{" "}
                                                                                View
                                                                            </div>
                                                                        </li> */}
                                                                        {checkPermission('store_erp', 'edit') && (line?.erp_details?.error || (line?.erp_details?.erp_synced === true && !line?.erp_details?.erp_reversed)) && (
                                                                            <li>
                                                                                <div
                                                                                    className="dropdown-item"
                                                                                    onClick={() => handleERPUploadAgain(line)}
                                                                                >
                                                                                    <i className="ti ti-upload" aria-hidden="true"></i> Upload Again
                                                                                </div>
                                                                            </li>
                                                                        )}
                                                                        {checkPermission('store_erp', 'edit') && line?.erp_details?.erp_synced === true && !line?.erp_details?.is_reversed && !line?.erp_details?.error && (
                                                                            <li>
                                                                                <div
                                                                                    className="dropdown-item"
                                                                                    onClick={() => {
                                                                                        handleSyncToERP(true, line);
                                                                                        // setIsReverseSync(true);
                                                                                    }}
                                                                                >
                                                                                    <i className="ti ti-sort-descending" aria-hidden="true"></i> Reverse Order
                                                                                </div>
                                                                            </li>
                                                                        )}
                                                                        {/* {!line?.erp_details?.erp_synced && !line?.erp_details?.erp_reversed && !line?.erp_details?.error && (
                                                                            <li>
                                                                                <div
                                                                                    className="dropdown-item"
                                                                                    onClick={() => handleERPRegenerate(line)}
                                                                                >
                                                                                    <i className="ti ti-recycle" aria-hidden="true"></i> Regenerate
                                                                                </div>
                                                                            </li>
                                                                        )} */}
                                                                        {checkPermission('store_erp', 'edit') && !line?.erp_details?.erp_reversed && (
                                                                            <li>
                                                                                <div
                                                                                    className="dropdown-item"
                                                                                    onClick={() => handleRegenerateAll('sales', line)}
                                                                                >
                                                                                    <i className="ti ti-recycle" aria-hidden="true"></i> Regenerate
                                                                                </div>
                                                                            </li>
                                                                        )}
                                                                    </ul>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan={10} className="text-center">
                                                            <img src={notFound} style={{ width: '5%' }} alt="Not Found" />
                                                            <p>No record found</p>
                                                        </td>
                                                    </tr>
                                                )}
                                            </>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            <div className="tabler-footer">
                                <div className="d-flex align-items-center">
                                    <div id="example1_info" role="status" className="table-result" aria-live="polite">
                                        <b>Total Records: {dataLength ? dataLength : '0'}</b>
                                    </div>
                                    {dataLength && dataLength > 0 ? (
                                        <>
                                            <div className='ms-3'>
                                                Show
                                                <select
                                                    value={itemPerPage}
                                                    onChange={(e) => {
                                                        setItemPerPage(Number(e.target.value))
                                                        setPage(1)
                                                    }}
                                                    className="mx-2"
                                                >
                                                    <option value={10}>10</option>
                                                    <option value={25}>25</option>
                                                    <option value={50}>50</option>
                                                    <option value={100}>100</option>
                                                </select>
                                                entries
                                            </div>
                                            <CustomPagination
                                                datalength={dataLength}
                                                itemperpage={itemPerPage}
                                                setPage={setPage}
                                                currentPage={page}
                                                pageRoute={[{ name: 'report', path: '/company/report/erp/list' }]}
                                            />
                                        </>
                                    ) : (
                                        ''
                                    )}
                                </div>
                            </div>
                        </div>
                    ) : 
                    reportType === 'payments' ? (
                        <div className="tabler-wrap">
                            <div className="table-responsive">
                                <table className="table table-custom">
                                    <thead>
                                        <tr>
                                            <th width="10%">
                                                <div className='d-flex gap-2'>
                                                        <input
                                                            type="checkbox"
                                                            id="selectAllForPayments" 
                                                            name='selectAllForPayments'
                                                            checked={selectAll}
                                                            onChange={(event) => selectAllRecordsToSync(event.target.checked)}
                                                            // disabled={line?.erp_details?.erp_synced || line?.erp_details?.erp_reversed}
                                                        />
                                                        <label htmlFor="selectAllForPayments">Select All</label>
                                                </div>
                                            </th>
                                            <th width="15%">Business Date</th>
                                            <th width="15%">Outlet Name</th>
                                            <th width="15%">Journal Batch Number </th>
                                            <th width="15%">Payment Method</th>
                                            <th width="15%">Total Price</th>
                                            <th width="15%">Description</th>
                                            <th width="15%">Status</th>
                                            <th width="15%">Error</th>
                                            <th width="15%">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loader ? (
                                            <tr>
                                                <td colSpan={10}>
                                                    <Loader />
                                                </td>
                                            </tr>
                                        ) : (
                                            <>
                                                {paymentLineData.flat().length > 0 ? (
                                                    paymentLineData.flat().map((payment, index) => (
                                                        <tr key={index}>
                                                            <td>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selectedPaymentRecords.includes(index)}
                                                                    onChange={() => handleCheckboxChange(index)}
                                                                    disabled={(payment?.erp_details != null && !payment?.erp_details?.error) || payment?.erp_details?.journal_batch_number}
                                                                />
                                                            </td>
                                                            <td>{formateDate(payment?.business_date)}</td>
                                                            <td>{capitalizeFirstLetter(outletData)}</td>
                                                            <td>{(payment?.erp_details?.journal_batch_number || "-")}</td>
                                                            <td>{capitalizeFirstLetter(payment?.payment_method)}</td>
                                                            <td>{(Number(payment?.total_amount).toFixed(2) + " " + "SAR")}</td>
                                                            <td>{payment.description}</td>
                                                            <td>{(payment?.erp_details?.error || !payment?.erp_details) ? <span className="text-danger">Not Uploaded</span> : <span className="text-success">Uploaded</span>}</td>
                                                            {payment?.erp_details?.error ? (
                                                                <td>
                                                                    <Whisper
                                                                        placement="top"
                                                                        controlId="control-id-hover"
                                                                        trigger="click"
                                                                        speaker={
                                                                            <Tooltip style={{
                                                                                maxHeight: '200px',
                                                                                overflowY: 'auto',
                                                                                whiteSpace: 'normal',
                                                                                maxWidth: '300px'
                                                                              }}>
                                                                                Unable to create free text payment header. {erpErrorText(payment?.erp_details?.error)}
                                                                            </Tooltip>
                                                                        }
                                                                    >
                                                                        <span className="field-more-info ms-1 cp">
                                                                            <i className="fas fa-exclamation-circle text-danger"></i>
                                                                        </span>
                                                                    </Whisper>
                                                                </td>
                                                            ) : (
                                                                <td>-</td>
                                                            )}
                                                            {/* More actions */}
                                                            <td className="text-end">
                                                                <div className="dropdown d-inline-block">
                                                                    <button
                                                                        className="btn btn-icon btn-secondary"
                                                                        type="button"
                                                                        data-bs-toggle="dropdown"
                                                                        aria-expanded="false"
                                                                    >
                                                                        <i className="ti ti-dots-vertical"></i>
                                                                    </button>
                                                                    <ul className="dropdown-menu">
                                                                        <li>
                                                                            <div
                                                                                className="dropdown-item"
                                                                                onClick={() => openModel('#paymentModal', payment)}
                                                                            >
                                                                                <i className="ti ti-eye" aria-hidden="true"></i> View
                                                                            </div>
                                                                        </li>

                                                                        {/* <li>
                                                                            <div
                                                                                className="dropdown-item"
                                                                                data-bs-toggle="modal" data-bs-target="#paymentModal"
                                                                                onClick={() => viewLineData(line)}
                                                                            >
                                                                                <i
                                                                                className="ti ti-eye"
                                                                                aria-hidden="true"
                                                                                ></i>{" "}
                                                                                View
                                                                            </div>
                                                                        </li> */}
                                                                        { checkPermission('store_erp', 'edit') && payment?.erp_details && (
                                                                            <li>
                                                                                <div
                                                                                    className="dropdown-item"
                                                                                    onClick={() => handleERPUploadAgain(payment)}
                                                                                >
                                                                                    <i className="ti ti-upload" aria-hidden="true"></i> Upload Again
                                                                                </div>
                                                                            </li>
                                                                        )}
                                                                        {/* {!payment?.erp_details && (
                                                                            <li>
                                                                                <div
                                                                                    className="dropdown-item"
                                                                                    onClick={() => handleERPPaymentMethodRegenerate(payment)}
                                                                                >
                                                                                    <i className="ti ti-recycle" aria-hidden="true"></i> Regenerate
                                                                                </div>
                                                                            </li>
                                                                        )} */}
                                                                        {checkPermission('store_erp', 'edit') && (
                                                                            <li>
                                                                                <div
                                                                                    className="dropdown-item"
                                                                                    onClick={() => handleRegenerateAll('payments', payment)}
                                                                                >
                                                                                    <i className="ti ti-recycle" aria-hidden="true"></i> Regenerate
                                                                                </div>
                                                                            </li>
                                                                        )}
                                                                    </ul>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan={10} className="text-center">
                                                            <img src={notFound} style={{ width: '5%' }} alt="Not Found" />
                                                            <p>No record found</p>
                                                        </td>
                                                    </tr>
                                                )}
                                            </>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            <div className="tabler-footer">
                                <div className="d-flex align-items-center">
                                    <div id="example1_info" role="status" className="table-result" aria-live="polite">
                                        <b>Total Records: {dataLength ? dataLength : '0'}</b>
                                    </div>
                                    {dataLength && dataLength > 0 ? (
                                        <>
                                            <div className='ms-3'>
                                                Show
                                                <select
                                                    value={itemPerPage}
                                                    onChange={(e) => {
                                                        setItemPerPage(Number(e.target.value))
                                                        setPage(1)
                                                    }}
                                                    className="mx-2"
                                                >
                                                    <option value={10}>10</option>
                                                    <option value={25}>25</option>
                                                    <option value={50}>50</option>
                                                    <option value={100}>100</option>
                                                </select>
                                                entries
                                            </div>
                                            <CustomPagination
                                                datalength={dataLength}
                                                itemperpage={itemPerPage}
                                                setPage={setPage}
                                                currentPage={page}
                                                pageRoute={[{ name: 'report', path: '/company/report/erp/list' }]}
                                            />
                                        </>
                                    ) : (
                                        ''
                                    )}
                                </div>
                            </div>
                        </div>
                    ) : reportType === 'commissions' ? (
                        <div className="tabler-wrap">
                            <div className="table-responsive">
                                <table className="table table-custom">
                                    <thead>
                                        <tr>
                                            <th width="%">
                                                <div className='d-flex gap-2'>
                                                        <input
                                                            type="checkbox"
                                                            id="selectAllForCommissions" 
                                                            name='selectAllForCommissions'
                                                            // checked={selectedRecords.includes(index)}
                                                            checked={selectAll}
                                                            onChange={(event) => selectAllRecordsToSync(event.target.checked)}
                                                            // disabled={line?.erp_details?.erp_synced || line?.erp_details?.erp_reversed}
                                                        />
                                                        <label htmlFor="selectAllForCommissions" className='font-dark'>Select All</label>
                                                </div>
                                            </th>
                                            <th width="%">Business Date</th>
                                            <th width="%">Outlet Name</th>
                                            <th width="%">Journal Batch Number </th>
                                            <th width="7%">Type</th>
                                            <th width="%">Aggregator Name</th>
                                            <th width="%">Commission</th>
                                            <th width="15%">Description</th>
                                            <th width="%">Status</th>
                                            <th width="%">Error</th>
                                            <th width="%">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loader ? (
                                            <tr>
                                                <td colSpan={10}>
                                                    <Loader />
                                                </td>
                                            </tr>
                                        ) : (
                                            <>
                                                {commissionsLineData && commissionsLineData?.flat().length > 0 ? (
                                                    commissionsLineData?.flat().map((commission, index) => (
                                                        <tr key={index}>
                                                            <td>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selectedCommissionsRecords.includes(index)}
                                                                    onChange={() => handleCheckboxChange(index)}
                                                                    disabled={commission?.erp_details != null && !commission?.erp_details?.error}
                                                                />
                                                            </td>
                                                            {/* <td>{formateDate(commission?.business_date)}</td> */}
                                                            <td>{formateDate(commission?.business_date)}</td>
                                                            <td>{capitalizeFirstLetter(outletData)}</td>
                                                            <td>{(commission?.erp_details?.journal_batch_number || "-")}</td>
                                                            <td>{capitalizeFirstLetter(commission?.type)}</td>
                                                            <td>{capitalizeFirstLetter(commission?.aggregators_configuration?.name) || "-"}</td>
                                                            <td>{(Number(commission?.commission)?.toFixed(2) + " " + "SAR")}</td>
                                                            <td>{commission?.description}</td>
                                                            
                                                            <td>{( !commission?.erp_details || commission?.erp_details?.error) ? <span className="text-danger">Not Uploaded</span> : <span className="text-success">Uploaded</span>}</td>
                                                            {commission?.erp_details?.error ? (
                                                                <td>
                                                                    <Whisper
                                                                        placement="top"
                                                                        controlId="control-id-hover"
                                                                        trigger="click"
                                                                        speaker={
                                                                            <Tooltip style={{
                                                                                maxHeight: '200px',
                                                                                overflowY: 'auto',
                                                                                whiteSpace: 'normal',
                                                                                maxWidth: '300px'
                                                                              }}>
                                                                                Unable to create free text commission header. {erpErrorText(commission?.erp_details?.error)}
                                                                            </Tooltip>
                                                                        }
                                                                    >
                                                                        <span className="field-more-info ms-1 cp">
                                                                            <i className="fas fa-exclamation-circle text-danger"></i>
                                                                        </span>
                                                                    </Whisper>
                                                                </td>
                                                            ) : (
                                                                <td>-</td>
                                                            )}
                                                            {/* More actions */}
                                                            <td className="text-end">
                                                                <div className="dropdown d-inline-block">
                                                                    <button
                                                                        className="btn btn-icon btn-secondary"
                                                                        type="button"
                                                                        data-bs-toggle="dropdown"
                                                                        aria-expanded="false"
                                                                    >
                                                                        <i className="ti ti-dots-vertical"></i>
                                                                    </button>
                                                                    <ul className="dropdown-menu">
                                                                        <li>
                                                                            <div
                                                                                className="dropdown-item"
                                                                                onClick={() => openModel('#commissionModal', commission)}
                                                                            >
                                                                                <i className="ti ti-eye" aria-hidden="true"></i> View
                                                                            </div>
                                                                        </li>

                                                                        {/* <li>
                                                                            <div
                                                                                className="dropdown-item"
                                                                                data-bs-toggle="modal" data-bs-target="#commissionModal"
                                                                                onClick={() => viewLineData(line)}
                                                                            >
                                                                                <i
                                                                                className="ti ti-eye"
                                                                                aria-hidden="true"
                                                                                ></i>{" "}
                                                                                View
                                                                            </div>
                                                                        </li> */}
                                                                        {/* commission?.erp_details?.journal_batch_number && (
                                                                            <li>
                                                                                <div
                                                                                    className="dropdown-item"
                                                                                    onClick={() => handleERPUploadAgain(commission)}
                                                                                >
                                                                                    <i className="ti ti-upload" aria-hidden="true"></i> Upload Again
                                                                                </div>
                                                                            </li>
                                                                        ) */}
                                                                        {checkPermission('store_erp', 'edit') && commission?.erp_details && (
                                                                            <li>
                                                                                <div
                                                                                    className="dropdown-item"
                                                                                    onClick={() => handleERPUploadAgain(commission)}
                                                                                >
                                                                                    <i className="ti ti-upload" aria-hidden="true"></i> Upload Again
                                                                                </div>
                                                                            </li>
                                                                        )}
                                                                        {/* {!commission?.erp_details && (
                                                                            <li>
                                                                                <div
                                                                                    className="dropdown-item"
                                                                                    onClick={() => handleERPPaymentMethodRegenerate(commission)}
                                                                                >
                                                                                    <i className="ti ti-recycle" aria-hidden="true"></i> Regenerate
                                                                                </div>
                                                                            </li>
                                                                        )} */}
                                                                        {/* {commission?.aggregator_id && (
                                                                            <li>
                                                                                <div
                                                                                    className="dropdown-item"
                                                                                    onClick={() => handleERPCommissionMethodRegenerate(commission)}
                                                                                >
                                                                                    <i className="ti ti-recycle" aria-hidden="true"></i> Regenerate
                                                                                </div>
                                                                            </li>
                                                                        )} */}
                                                                        {checkPermission('store_erp', 'edit') && (
                                                                            <li>
                                                                                <div
                                                                                    className="dropdown-item"
                                                                                    onClick={() => handleRegenerateAll('commissions', commission)}
                                                                                >
                                                                                    <i className="ti ti-recycle" aria-hidden="true"></i> Regenerate
                                                                                </div>
                                                                            </li>
                                                                        )}
                                                                    </ul>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan={10} className="text-center">
                                                            <img src={notFound} style={{ width: '5%' }} alt="Not Found" />
                                                            <p>No record found</p>
                                                        </td>
                                                    </tr>
                                                )}
                                            </>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            <div className="tabler-footer">
                                <div className="d-flex align-items-center">
                                    <div id="example1_info" role="status" className="table-result" aria-live="polite">
                                        <b>Total Records: {dataLength ? dataLength : '0'}</b>
                                    </div>
                                    {dataLength && dataLength > 0 ? (
                                        <>
                                            <div className='ms-3'>
                                                Show
                                                <select
                                                    value={itemPerPage}
                                                    onChange={(e) => {
                                                        setItemPerPage(Number(e.target.value))
                                                        setPage(1)
                                                    }}
                                                    className="mx-2"
                                                >
                                                    <option value={10}>10</option>
                                                    <option value={25}>25</option>
                                                    <option value={50}>50</option>
                                                    <option value={100}>100</option>
                                                </select>
                                                entries
                                            </div>
                                            <CustomPagination
                                                datalength={dataLength}
                                                itemperpage={itemPerPage}
                                                setPage={setPage}
                                                currentPage={page}
                                                pageRoute={[{ name: 'report', path: '/company/report/erp/list' }]}
                                            />
                                        </>
                                    ) : (
                                        ''
                                    )}
                                </div>
                            </div>
                        </div>
                    ) : null
                    }

                </div>
            </div>

            <div className="modal fade" id="salesModal" aria-labelledby="salesModalLabel" aria-hidden="true">
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h2 className="modal-title" id="salesModalLabel">Sales ERP Line</h2>
                            <button type="button" className="btn-close" onClick={(e) => { closeModel('#salesModal'); }} data-bs-dismiss="modal" aria-label="Close"></button>
                        </div>
                        <div className="modal-body">
                            <table id="simple-table" className="table table-custom  ">
                                <tbody>
                                    <tr>
                                        <td>Business Data</td>
                                        <td className='text-end'>{formateDate(lineData?.business_date)}</td>
                                    </tr>
                                    <tr>
                                        <td>Outlet</td>
                                        <td className='text-end'>{capitalizeAllFirstLetters(outletName)}</td>
                                    </tr>
                                    <tr>
                                        <td>Main Account Display Value</td>
                                        <td className='text-end'>{lineData?.main_account_display_value}</td>
                                    </tr>
                                    <tr>
                                        <td>General Number</td>
                                        <td className='text-end'>{lineData?.erp_details?.invoice_identifier || '-'}</td>
                                    </tr>
                                    <tr>
                                        <td>Description</td>
                                        <td className='text-end'>{lineData?.description}</td>
                                    </tr>
                                    <tr>
                                        <td>Customer Account</td>
                                        <td className='text-end'>{lineData?.customer_account || '-'}</td>
                                    </tr>
                                    <tr>
                                        <td>Unit Price</td>
                                        <td className='text-end'>{lineData?.unit_price || '-'}</td>
                                    </tr>
                                    <tr>
                                        <td>Status</td>
                                        <td className='text-end'>
                                            {!lineData?.erp_details || lineData?.erp_details?.error ? (
                                                <span className="text-danger">Not Uploaded</span>
                                            ) : lineData?.erp_details?.erp_synced === true && !lineData?.erp_details?.erp_reversed ? (
                                                <span className="text-success">Uploaded</span>
                                            ) : (
                                                <span className="text-warning">Reversed</span>
                                            )}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>Error</td>
                                            {
                                                lineData?.erp_details?.error ? (
                                                    <td>
                                                        <Whisper
                                                            placement="top"
                                                            controlId="control-id-hover"
                                                            trigger="click"
                                                            container={document.body}   // 👈 IMPORTANT
                                                            flip={false}  
                                                            preventOverflow
                                                            speaker={
                                                                <Tooltip style={{
                                                                    maxHeight: '200px',
                                                                    overflowY: 'auto',
                                                                    whiteSpace: 'normal',
                                                                    // maxWidth: '300px'
                                                                    }}>
                                                                    Unable to create free text invoice header. {erpErrorText(lineData?.erp_details?.error)}
                                                                </Tooltip>
                                                            }
                                                        >
                                                            <span className="field-more-info ms-1 cp">
                                                                <i className="fas fa-exclamation-circle text-danger"></i>
                                                            </span>
                                                        </Whisper>
                                                    </td>
                                                ) : (
                                                    <td className='text-end'>-</td>
                                                )
                                            }
                                    </tr>
                                </tbody>            
                            </table>
                           
                            {/* <div className="mb-3">
                                <label for="name_ar" className="col-form-label">Name (Arabic)<span className="requirestar">*</span></label>
                                <input type="text" className="form-control" name="name_ar" id="name_ar" placeholder='Enter Name (Arabic)' onChange={handleChange} onBlur={handleBlur} value={values.name_ar} autoComplete='off' />
                                <span className='text-danger d-flex text-left'>{errors.name_ar && touched.name_ar && errors.name_ar}</span>
                            </div>
                            
                            <div className="form-check form-switch w-25">
                                <input className="form-check-input" type="checkbox" id="status" name="status" onChange={handleChange} onBlur={handleBlur} checked={values.status == 'on' ? 'checked' : ''} autoComplete='off' />
                                <label className="form-check-label" for="status">Status</label>
                            </div> */}
                        </div>
                        {/* <div className="modal-footer">
                            <button type="button" className="btn btn-outline-primary" style={{ width: '48%' }} data-bs-dismiss="modal" onClick={(e) => { closeModel('#salesModal'); }}>Cancel</button>
                            <button type="submit" className="btn btn-primary" style={{ width: '48%' }}>{actionType === 'edit' ? 'Update' : 'Add'}</button>
                        </div> */}

                        {/* <form onSubmit={handleSubmit}>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label for="name" className="col-form-label">Name <span className="requirestar">*</span></label>
                                    <input type="text" className="form-control" id="name" name="name" placeholder='Enter Name' onChange={handleChange} onBlur={handleBlur} value={values.name} autoComplete='off' />
                                    <span className='text-danger d-flex text-left'>{errors.name && touched.name && errors.name}</span>
                                </div>
                                <div className="mb-3">
                                    <label for="name_ar" className="col-form-label">Name (Arabic)<span className="requirestar">*</span></label>
                                    <input type="text" className="form-control" name="name_ar" id="name_ar" placeholder='Enter Name (Arabic)' onChange={handleChange} onBlur={handleBlur} value={values.name_ar} autoComplete='off' />
                                    <span className='text-danger d-flex text-left'>{errors.name_ar && touched.name_ar && errors.name_ar}</span>
                                </div>
                             
                                <div className="form-check form-switch w-25">
                                    <input className="form-check-input" type="checkbox" id="status" name="status" onChange={handleChange} onBlur={handleBlur} checked={values.status == 'on' ? 'checked' : ''} autoComplete='off' />
                                    <label className="form-check-label" for="status">Status</label>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-outline-primary" style={{ width: '48%' }} data-bs-dismiss="modal" onClick={(e) => { closeModel('addCardModal'); addFormRef?.current?.resetForm() }}>Cancel</button>
                                <button type="submit" className="btn btn-primary" style={{ width: '48%' }}>{actionType === 'edit' ? 'Update' : 'Add'}</button>
                            </div>
                        </form> */}
                    </div>
                </div>
            </div>

            <div className="modal fade" id="paymentModal" aria-labelledby="paymentModalLabel" aria-hidden="true">
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h2 className="modal-title" id="paymentModalLabel">Payment ERP Line</h2>
                            <button type="button" className="btn-close" onClick={(e) => { closeModel('#paymentModal'); }} data-bs-dismiss="modal" aria-label="Close"></button>
                        </div>
                        <div className="modal-body">
                            <table id="simple-table" className="table table-custom  ">
                                <tbody>
                                    <tr>
                                        <td>Business Data</td>
                                        <td className='text-end'>{formateDate(lineData?.business_date)}</td>
                                    </tr>
                                    <tr>
                                        <td>Outlet</td>
                                        <td className='text-end'>{capitalizeAllFirstLetters(outletName)}</td>
                                    </tr>
                                    <tr>
                                        <td>Journal Batch Number</td>
                                        <td className='text-end'>{lineData?.erp_details?.journal_batch_number || "-"}</td>
                                    </tr>
                                    <tr>
                                        <td>Payment Method</td>
                                        <td className='text-end'>{capitalizeFirstLetter(lineData?.payment_method) || '-'}</td>
                                    </tr>
                                    <tr>
                                        <td>Total Price</td>
                                        <td className='text-end'>{(Number(lineData?.total_amount).toFixed(2) + " " + "SAR")}</td>
                                    </tr>
                                    <tr>
                                        <td>Description</td>
                                        <td className='text-end'>{lineData?.description}</td>
                                    </tr>
                                    <tr>
                                        <td>Status</td>
                                        <td className='text-end'>
                                            {
                                                (lineData?.erp_details?.error || !lineData?.erp_details) 
                                                    ? <span className="text-danger">Not Uploaded</span> 
                                                    : <span className="text-success">Uploaded</span>
                                            }                                            
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>Error</td>
                                            {lineData?.erp_details?.error ? (
                                                <td>
                                                    <Whisper
                                                        placement="top"
                                                        controlId="control-id-hover"
                                                        trigger="click"
                                                        speaker={
                                                            <Tooltip style={{
                                                                maxHeight: '200px',
                                                                overflowY: 'auto',
                                                                whiteSpace: 'normal',
                                                                maxWidth: '300px'
                                                                }}>
                                                                Unable to create free text payment header. {erpErrorText(lineData?.erp_details?.error)}
                                                            </Tooltip>
                                                        }
                                                    >
                                                        <span className="field-more-info ms-1 cp">
                                                            <i className="fas fa-exclamation-circle text-danger"></i>
                                                        </span>
                                                    </Whisper>
                                                </td>
                                            ) : (
                                                <td className='text-end'>-</td>
                                            )}
                                    </tr>
                                </tbody>            
                            </table>
                        </div>
                       
                    </div>
                </div>
            </div>

            <div className="modal fade" id="commissionModal" aria-labelledby="commissionModalLabel" aria-hidden="true">
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h2 className="modal-title" id="commissionModalLabel">Commission ERP Line</h2>
                            <button type="button" className="btn-close" onClick={(e) => { closeModel('#commissionModal'); }} data-bs-dismiss="modal" aria-label="Close"></button>
                        </div>
                        <div className="modal-body">
                            <table id="simple-table" className="table table-custom  ">
                                <tbody>
                                    <tr>
                                        <td>Business Data</td>
                                        <td className='text-end'>{formateDate(lineData?.business_date)}</td>
                                    </tr>
                                    <tr>
                                        <td>Outlet</td>
                                        <td className='text-end'>{capitalizeAllFirstLetters(outletName)}</td>
                                    </tr>
                                    <tr>
                                        <td>Journal Batch Number</td>
                                        <td className='text-end'>{lineData?.erp_details?.journal_batch_number || "-"}</td>
                                    </tr>
                                    
                                    <tr>
                                        <td>Type</td>
                                        <td className='text-end'>{capitalizeFirstLetter(lineData?.type) || '-'}</td>
                                    </tr>
                                    <tr>
                                        <td>Aggregator Name</td>
                                        <td className='text-end'>{capitalizeFirstLetter(lineData?.aggregators_configuration?.name) || "-"}</td>
                                    </tr>
                                    <tr>
                                        <td>Commission</td>
                                        <td className='text-end'>{(Number(lineData?.commission)?.toFixed(2) + " " + "SAR")}</td>
                                    </tr>
                                    <tr>
                                        <td>Description</td>
                                        <td className='text-end'>{lineData?.description}</td>
                                    </tr>
                                    <tr>
                                        <td>Aggregator Bank Account Number</td>
                                        <td className='text-end'>{lineData?.aggregator_bank_account_number || '-'}</td>
                                    </tr>
                                    <tr>
                                        <td>Status</td>
                                        <td className='text-end'>
                                            {
                                                ( !lineData?.erp_details || lineData?.erp_details?.error) 
                                                    ? <span className="text-danger">Not Uploaded</span> 
                                                    : <span className="text-success">Uploaded</span>                                        
                                            }  
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>Error</td>
                                        {lineData?.erp_details?.error ? (
                                            <td>
                                                <Whisper
                                                    placement="top"
                                                    controlId="control-id-hover"
                                                    trigger="click"
                                                    speaker={
                                                        <Tooltip style={{
                                                            maxHeight: '200px',
                                                            overflowY: 'auto',
                                                            whiteSpace: 'normal',
                                                            maxWidth: '300px'
                                                            }}>
                                                            Unable to create free text commission header. {erpErrorText(lineData?.erp_details?.error)}
                                                        </Tooltip>
                                                    }
                                                >
                                                    <span className="field-more-info ms-1 cp">
                                                        <i className="fas fa-exclamation-circle text-danger"></i>
                                                    </span>
                                                </Whisper>
                                            </td>
                                        ) : (
                                            <td className='text-end'>-</td>
                                        )}
                                    </tr>
                                </tbody>            
                            </table>
                        </div>
                       
                    </div>
                </div>
            </div>

        </>
    );
}

export default ERPConfigurationList;
